import { Injectable } from "@angular/core";
import { AssessmentGoal } from "../shared/models/assessment-goal";
import {
  AssessmentProject,
  projectExpandStates,
  ProjectViewStates
} from "../shared/models/assessment-project";
import { AssessmentTemplate } from "../shared/models/assessment-template";
import { ApiCallService } from "./api-call.service";
import { ErrorMessageService } from "./error-message.service";
import { AssessmentObjective } from "../shared/models/assessment-objective";
import { AssessmentMeasure } from "../shared/models/assessment-measure";
import { AssessmentTarget } from "../shared/models/assessment-target";
import { AssessmentActionPlan } from "../shared/models/assessment-action-plan";
import { AssessmentActionPlanItem } from "../shared/models/assessment-action-plan-item";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import {MatDialog} from "@angular/material/dialog";
import { DeleteWarningComponent } from "../shared/warning-popups/delete-warning/delete-warning.component";
import { DeleteProjectWarningComponent } from "../shared/warning-popups/delete-project-warning/delete-project-warning.component";
import { SourceOfEvidence } from "../shared/models/source-of-evidence";
import { DeleteAttachmentWarningComponent } from "../shared/warning-popups/delete-attachment-warning/delete-attachment-warning.component";
import {RemoteResponse} from "../shared/remote-response";
import { ProjectPart } from "../shared/models/project-part";
import { GeneralWarningModalComponent } from "../components/shared/modals/general-warning-modal/general-warning-modal.component";
import { ProjectView } from "../shared/classes/project-view.enum";
import {first} from "rxjs/operators";
import {Router} from "@angular/router";

@Injectable( {
  providedIn: 'root'
} )
export class AssessmentService {
  public service = this;

  private projectRequestKeySource = new BehaviorSubject<string>("");
  projectRequestKey = this.projectRequestKeySource.asObservable();

  private projectSource = new BehaviorSubject<AssessmentProject>(null);
  project = this.projectSource.asObservable();

  private projectLockedSource = new BehaviorSubject<boolean>(false);
  projectLocked = this.projectLockedSource.asObservable();

  private expandedAllSource = new Subject<boolean>();
  expandedAll = this.expandedAllSource.asObservable();

  private mappingExpandedAllSource = new Subject<boolean>();
  mappingExpandedAll = this.mappingExpandedAllSource.asObservable();

  private leftClosedSource = new BehaviorSubject<boolean>(false);
  leftClosed = this.leftClosedSource.asObservable();
  private rightClosedSource = new BehaviorSubject<boolean>(false);
  rightClosed = this.rightClosedSource.asObservable();

  private maxRightSource = new BehaviorSubject<boolean>(true);
  maxRight = this.maxRightSource.asObservable();

  private lockedSource = new BehaviorSubject<boolean>(false);
  locked = this.lockedSource.asObservable();
  private waslockedSource = new BehaviorSubject<boolean>(false);
  waslocked = this.waslockedSource.asObservable();

  private templateSource = new BehaviorSubject<AssessmentTemplate>(new AssessmentTemplate());
  template = this.templateSource.asObservable();

  private goalSource = new Subject<AssessmentGoal>();
  goalObserver = this.goalSource.asObservable();

  private outcomeSource = new Subject<AssessmentObjective>();
  outcomeObserver = this.outcomeSource.asObservable();

  private reportingPeriodsSource = new Subject<{requestKey: string, name: string}[]>();
  reportingPeriods = this.reportingPeriodsSource.asObservable();

  private improvementsAchievedListSource = new BehaviorSubject<any[]>([]);
  improvementsAchievedList = this.improvementsAchievedListSource.asObservable();

  private achievementStatusesListSource = new BehaviorSubject<any[]>([]);
  achievementStatusesList = this.achievementStatusesListSource.asObservable();

  private improvementPlanStatusesSource = new BehaviorSubject<any[]>([]);
  improvementPlanStatuses = this.improvementPlanStatusesSource.asObservable();

  private currentViewSource = new BehaviorSubject<ProjectView>(0);
  currentView = this.currentViewSource.asObservable();

  improvementsAchievedListUntouched: any[];
  sourcesOfEvidence: SourceOfEvidence[];

  expandedStates: projectExpandStates;

  userAccessRights: any;
  timeoutId: number;

  slots: any[] = [];

  elementsDeletedCounter = {
    goals: 0,
    outcomes: 0,
    measures: 0,
    targets: 0,
    actionPlans: 0,
    actions: 0,
    siStandards: 0,
    siGeneralEducationElements: 0,
    siInstitutionalPriorities: 0,
    siStrategicInitiatives: 0,
    slots: 0
  };

  readonly emptyOption = [{
    name: 'Not Set',
    requestKey: '',
    description: '',
    type: {name: 'a'},
    class: "not-set"
  }];
  readonly emptyImprovementList = [{
    name: 'None Set',
    description: 'None Set',
    requestKey: "",
    type: {
      name: "",
      requestKey: ""
    }
  }];
  readonly statusColors = {
    '': 'not-set weaveSelectOption',
    '{B4F49A53-2FD6-4535-AC4B-3578B9424286}' : 'not-started weaveSelectOption',
    '{208F1733-9990-4A42-B075-9CCF09C8E089}' : 'draft weaveSelectOption',
    '{66FFB771-6D7F-484C-B566-637B3B29C43D}' : 'in-review weaveSelectOption',
    '{E97137A9-85AD-449B-9706-FB4F94C1CCAD}' : 'final weaveSelectOption',
    '{E6A94D79-E4D9-430D-B967-B9B656C49D32}' : 'planned weaveSelectOption',
    '{E800CF4A-715F-4397-A904-07F516F2B489}' : 'completed weaveSelectOption',
    '{28D007CC-5BD5-4081-A719-A3B531BE6E49}' : 'nothing-entered weaveSelectOption',
    '{9556AABC-846F-4049-BCBA-143CE0FB5641}' : 'not-reported-this-cycle weaveSelectOption',
    '{77907323-F78B-4231-9DC2-708C3C43DF8C}' : 'not-met weaveSelectOption',
    '{E6E02298-6490-430B-9F9C-862051B44DB8}' : 'partially-met weaveSelectOption',
    '{83A2916E-D7F9-4E60-8500-D5ED9386FECC}' : 'met weaveSelectOption',
    '{04A4FAF0-13B6-4D7F-9DEE-4C22DB6E79DE}' : 'exceeded weaveSelectOption',
  };

  updateProjectRequestKey(projectRequestKey: string) {
    this.projectRequestKeySource.next(projectRequestKey);
  }
  updateProject(project: AssessmentProject) {
    this.projectSource.next(project);
  }
  updateExpandedAll(expandedAll: boolean) {
    this.expandedAllSource.next(expandedAll);
  }
  updateMappingExpandedAll(expandedAll: boolean) {
    this.mappingExpandedAllSource.next(expandedAll);
  }

  updateLeftClosed(leftClosed: boolean, projectRequestKey: string) {
    this.leftClosedSource.next(leftClosed);
    this.setViewStates(projectRequestKey);
  }

  updateRightClosed(rightClosed: boolean, projectRequestKey: string) {
    this.rightClosedSource.next(rightClosed);
    this.setViewStates(projectRequestKey);
  }

  updateRightMax(maxRight: boolean, projectRequestKey: string){
    this.maxRightSource.next(maxRight);
    this.setViewStates(projectRequestKey);
  }

  updateLocked(locked: boolean) {
    this.lockedSource.next(locked);
  }

  updateWasLocked(locked: boolean) {
    this.waslockedSource.next(locked);
  }

  updateTemplate(template: AssessmentTemplate) {
    this.templateSource.next(template);
  }

  setImprovementsAchievedList(list) {
    this.improvementsAchievedListSource.next(list);
  }

  setAchievementStatusesList(list: any[]) {
    this.achievementStatusesListSource.next(list);
  }

  setSourcesOfEvidence(list) {
    this.sourcesOfEvidence = list;
  }
  getSourcesOfEvidence() {
    return this.sourcesOfEvidence;
  }

  getStatusColor(requestKey: string) {
    return this.statusColors[requestKey];
  }

  setImprovementPlanStatuses(list) {
    this.improvementPlanStatusesSource.next(list);
  }
  getImprovementPlanStatuses() {
    return this.improvementPlanStatuses;
  }

  updateCurrentView(viewName:ProjectView) {
    this.currentViewSource.next(viewName);
  }


  constructor( private apiCallService: ApiCallService,
               private errorMessage: ErrorMessageService,
               public dialog: MatDialog,
               private router: Router,
  ) {}

  /* helping functions */
  increaseMaxSortOrder( currentMaxSortOrder: string ) {
    if( currentMaxSortOrder === '9999' ) {
      return currentMaxSortOrder;
    }
    return this.paddy( parseInt( currentMaxSortOrder ) + 1 )
  };

  scrollToElem( element:ProjectPart ) { //todo cannot have ElementRef in a service
    if(element.htmlId && document.getElementById( element.htmlId )) {
      setTimeout( () => {
        document.getElementById( element.htmlId ).scrollIntoView( { behavior: "smooth", block: "start" } );
      }, 50 );
    }
  };
  scrollToId( elementId:string ) { //todo cannot have ElementRef in a service
    if(document.getElementById(elementId)) {
      setTimeout( () => {
        document.getElementById( elementId ).scrollIntoView( { behavior: "smooth", block: "start" } );
      }, 50 );
    }
  };

  paddy( num ) {
    return ( '0000' + num ).slice( -4 );
  };

  /* get project */
  getAssessmentProjectData(requestKey, polling, blockUI){
    if(!polling) {
      blockUI.start();
      this.updateProjectRequestKey(requestKey);
      this.updateProject( new AssessmentProject() );
    }
    this.apiCallService.getData('/editAssessmentProject/', {pcRequestKey : requestKey})
      .subscribe((response) => {
        if (response && response.success && response.data[0] && response.data[0].dataSet ) {
          let currentProject:AssessmentProject = this.convertProject(response.data[0].dataSet);
          currentProject.ILOCount = response.data[11].recordCount;
          this.updateProject( currentProject );
          let tempTemplate = currentProject.projectTemplate.templateData;
          this.updateTemplate(tempTemplate);
          //let outcomesList = response.data[0].dataSet['objectiveList'];
          //this.updateOutcomes(outcomesList)

          //this.improvementsAchievedTypes = response.data[5].dataSet; //todo XXX what is this supposed to do???
          this.setAchievementStatusesList(response.data[6].dataSet);
          this.updateImprovementsAchievedList(response.data[7].dataSet);
          this.updateSourcesOfEvidence(response.data[8].dataSet);
          this.setImprovementPlanStatuses(response.data[9].dataSet);
          this.userAccessRights = response.data[10].dataSet;

          this.expandedStates = this.getExpandStates(requestKey);
          if(this.expandedStates){
            this.projectLoadExpandedStates(currentProject, this.expandedStates);
          }
          else{
            this.expandedStates = new projectExpandStates();
            this.updateExpandedStatesFromProject(this.expandedStates, currentProject, true);
          }

          //Disable inputs if user has not edit access or if the project is locked for editing
          if(currentProject.editLocked || !currentProject.reportingPeriod.editable || !this.userAccessRights.allowEdit) {
            this.updateLocked(true);
            if(currentProject.editLocked){
              this.timeoutId = window.setTimeout(() => {
                this.getAssessmentProjectData(requestKey, true, blockUI);
              }, 30000);
            }
          }

          currentProject.canRemoveTeamMember = currentProject.reportingPeriod.editable === true && this.userAccessRights.allowEdit;
          //this.getSlots();

          if(this.userAccessRights.allowEdit && currentProject.reportingPeriod.editable) {
            //If project is unlocked for editing, then lock it
            if (!currentProject.editLocked) {
              if(this.projectLockedSource.value){
                this.updateWasLocked(true);
              }
              this.updateLocked(false);
              this.projectEditLockHeartBeat(requestKey);
            }
            else { //Check every 30 seconds for changes
              setTimeout(() => {
                this.getAssessmentProjectData(requestKey, true, blockUI);
              }, 30000);
            }
          }

        } else { //no response
          if( !polling ) {
            this.errorMessage.showErrorModal("There was a problem with getting the assessment project data", response.dataError);
            blockUI.stop();
          }
        }

        if( !polling ) {
          setTimeout(() =>{
            blockUI.stop();
          }, 500);
        }
      });
  };

  projectEditLockHeartBeat(projectRequestKey) {
    this.apiCallService.getData('/remotes/projectEditLockHeartBeat/', {pcRequestKey : projectRequestKey}).subscribe((response) => {
      if(!response.success){
        this.errorMessage.showErrorModal("There was a problem with locking the project for edit", response.dataError);
      } else {
        this.timeoutId = window.setTimeout(() => {
          this.projectEditLockHeartBeat(projectRequestKey);
        }, 30000);
      }
    });
  };

  projectDestroyTimeout() {
    console.log(this.timeoutId)
    if(this.timeoutId){
      clearTimeout(this.timeoutId);
    }
  }

  /* deletes */
  deleteProjectWarning(project) {
    const dialogRef = this.dialog.open( DeleteProjectWarningComponent, {
      width: '600px',
      data: {project}
    } );

    dialogRef.afterClosed().subscribe(result => {
      if(result) {
        //make the actual delete
        this.apiCallService.getData(
          '/remotes/deleteEffectProject/',
          {pcRequestKey : project.requestKey}
        ).subscribe((response) => {
          if( response && response.success ) {
            this.projectDestroyTimeout();
            this.router.navigate(['/projects']);
          } else {
            this.errorMessage.showErrorModal( "There was a problem with deleting this element", response.dataError );
          }
        });
      }
    });
  }

  deleteActionPlanWarning(element, parentElement, projectRequestKey) {
    const dialogRef = this.dialog.open( DeleteWarningComponent, {
      width: '600px',
      data: {element}
    } );

    dialogRef.afterClosed().subscribe(result => {
      if(result) {
        //make the actual delete
        this.apiCallService.getData(
          '/remotes/deleteEffectActionPlan/',
          {pcProjectRequestKey : projectRequestKey,
           pcRequestKey : element.requestKey,
           pcObjectiveID: parentElement.id
          }
        ).subscribe((response) => {
          if( response && response.success ) {
            //removing from view, too
            parentElement.actionPlan = null;
          } else {
            this.errorMessage.showErrorModal( "There was a problem with deleting this element", response.dataError );
          }
        });
      }
    });
  }

  deleteElementWarning(element, parentElement, parentListName, remoteMethodAddOn, projectRequestKey, template = null):Observable<boolean> {
    let subject = new Subject<boolean>();
    let ready = this.getSlots(projectRequestKey);
    ready.subscribe(data => {
      if(data) {
        this.initializeDeleteCounters();
        this.countDeleteElements(element);
        const dialogRef = this.dialog.open(DeleteWarningComponent, {
          width: '600px',
          data: {
            element: element,
            deleteElementsCounterString: this.showDeleteElements( this.template ),
            moreDeleted :this.countDeleteItems(),
            affectedElementsCounter: this.countSlotsAffected()
          }
        });

        dialogRef.afterClosed().subscribe(result => {
          if(result) {
            //make the actual delete
            let parentId = "";
            if(parentElement !== null ){
              parentId = parentElement.id;
            }
            return this.apiCallService.getData(
              remoteMethodAddOn, {
                pcProjectRequestKey: projectRequestKey, //todo coordinate with jay to delete this
                pcRequestKey: element.requestKey,
                pcObjectiveID: parentId //todo this is not used?
              } ).subscribe( ( response ) => {
              if( response && response.success ) {
                //this.project ... filter out the request key!!
                let project = this.projectSource.getValue();
                if(parentListName) {
                  switch ( parentListName ) {
                    case "measuresList":
                      project.measuresList = project.measuresList.filter( e => {
                        return e.requestKey !== element.requestKey;
                      } );
                      break;
                    case "goalList":
                      project.goalList = project.goalList.filter( e => {
                        return e.requestKey !== element.requestKey;
                      } );
                      break;
                    case "goalObjectiveList":
                      if(parentElement) {
                        project.goalList.forEach( goal => {
                          if( goal.requestKey === parentElement.requestKey ) {
                            goal.goalObjectiveList = goal.goalObjectiveList.filter( function( e ) {
                              return e.requestKey !== element.requestKey;
                            } );
                          }
                        } );
                      }
                      break;
                    default: //the rest for now are not transferred to other pages, so we can keep them local
                      if(parentElement) {
                        parentElement[ parentListName ] = parentElement[ parentListName ].filter( function( e ) {
                          return e.requestKey !== element.requestKey;
                        } );
                      }
                      break;
                  }
                }
                this.updateProject(project);
                this.initializeDeleteCounters();
                subject.next(true);
                return subject.asObservable();
              } else {
                this.errorMessage.showErrorModal( "There was a problem with deleting this element", response.dataError );
                this.initializeDeleteCounters();
                subject.next(false);
                return subject.asObservable();
              }
            });
          } else {
            subject.next(false);
            return subject.asObservable();
          }
        });
      }
    });
    subject.next(false);
    return subject.asObservable();
  }

  deleteProjectAttachmentWarning(element, projectRequestKey):Observable<boolean> {
    const dialogRef = this.dialog.open(DeleteAttachmentWarningComponent, {
      width: '600px',
      data: {element: element}
    });

    let subject= new Subject<boolean>();

    dialogRef.afterClosed().subscribe(result => {
      if(result) {
        this.apiCallService.getData('/remotes/deleteEffectProjectDocument/', {projectRequestKey : projectRequestKey, documentRequestKey : element.requestKey }).subscribe((response) => {
          if(response && response.success) {
            subject.next(true);
          } else {
            this.errorMessage.showErrorModal( "There was a problem with deleting this element", response.dataError );
            subject.next(false);
          }
        })
      };
    });
    return subject.asObservable();
  }

  removeSIWarning(elements:any[], projectRequestKey:string, parentElementListName:string, objective:AssessmentObjective, massive: boolean = true):Observable<boolean> {
    let message = "You are about to remove the Supported Initiatives you unchecked from this project.";
    if(!massive) {
      message = "You are about to remove this Supported Initiative."
    }
    const dialogRef = this.dialog.open(GeneralWarningModalComponent, {
      width: '600px',
      data: {
        title: "Are you sure?",
        message: message,
        question: "Do you want to continue?",
        confirmText: "Remove",
        cancelText: "Cancel"
      }
    });

    let subject= new Subject<boolean>();

    dialogRef.afterClosed().subscribe(result => {
      if(result) {
        let allGood = true;
        let errorMessages = null;
        elements.forEach(element=>{
          this.deleteSupportedInitiative(element, objective, parentElementListName, projectRequestKey).subscribe((response) => {
            if(response && response.success) {
                objective[parentElementListName] = objective[parentElementListName].filter(function (e) {
                  return e.element.requestKey !== element.requestKey;
                });
            } else {
              allGood = false;
              errorMessages = response.dataError;
            }
          })
        });
        if(allGood) {
          subject.next(true);
        } else {
          let errMess = "There was a problem with removing a Supported Initiative";
          if( !massive ) {
           errMess = "There was a problem with removing this Supported Initiative";
          }
          this.errorMessage.showErrorModal( errMess, errorMessages );

          subject.next(false);
        }
      }
    });
    return subject.asObservable();
  }

  deleteSupportedInitiative(element, objective:AssessmentObjective, parentElementListName:string, projectRequestKey:string) {
    let parentList = objective[parentElementListName];
    let accessRequestKey = parentList.filter(function(e){
      return e.element.requestKey === element.requestKey;
    })[0].requestKey;
    return this.apiCallService.getData('/remotes/deleteSupportedInitiative/',
      {pcProjectRequestKey : projectRequestKey, pcRequestKey : accessRequestKey}
    );
  };

  countDeleteItems() {
    return this.elementsDeletedCounter.goals +
      this.elementsDeletedCounter.outcomes +
      this.elementsDeletedCounter.measures +
      this.elementsDeletedCounter.targets +
      this.elementsDeletedCounter.actionPlans +
      this.elementsDeletedCounter.actions +
      this.elementsDeletedCounter.siStandards +
      this.elementsDeletedCounter.siGeneralEducationElements +
      this.elementsDeletedCounter.siInstitutionalPriorities +
      this.elementsDeletedCounter.siStrategicInitiatives;
  };
  countSlotsAffected() {
    return this.elementsDeletedCounter.slots;
  }

  showDeleteElements(template) {
    let messageArray = [];
    let tempMessage = "";
    if( this.elementsDeletedCounter.goals !== 0 ) {
      tempMessage = this.elementsDeletedCounter.goals + " ";
      if(template !== null) {
        tempMessage += template.goalCaption ? template.goalCaption : 'Goals';
      } else {
        tempMessage += 'Goals';
      }
      messageArray.push(tempMessage);
    }
    if( this.elementsDeletedCounter.outcomes !== 0 ) {
      tempMessage = this.elementsDeletedCounter.outcomes + " ";
      if(template !== null) {
        tempMessage += template.objectiveCaption ? template.objectiveCaption : 'Outcomes';
      } else {
        tempMessage += 'Outcomes';
      }
      messageArray.push(tempMessage);
    }
    if( this.elementsDeletedCounter.measures !== 0 ) {
      tempMessage = this.elementsDeletedCounter.measures + " ";
      if(template !== null) {
        tempMessage += template.measureCaption ? template.measureCaption : 'Measures';
      } else {
        tempMessage += 'Measures';
      }
      messageArray.push(tempMessage);
    }
    if( this.elementsDeletedCounter.targets !== 0 ) {
      tempMessage = this.elementsDeletedCounter.targets + " ";
      if(template !== null) {
        tempMessage += template.qualitativeTargetCaption ? template.qualitativeTargetCaption : 'Targets';
      } else {
        tempMessage += 'Targets';
      }
      messageArray.push(tempMessage);
    }
    if( this.elementsDeletedCounter.actionPlans !== 0 ) {
      tempMessage = this.elementsDeletedCounter.actionPlans + " ";
      if(template !== null) {
        tempMessage += template.actionPlanCaption ? template.actionPlanCaption : 'Action Plans';
      } else {
        tempMessage += 'Action Plans';
      }
      messageArray.push(tempMessage);
    }
    if( this.elementsDeletedCounter.actions !== 0 ) {
      tempMessage = this.elementsDeletedCounter.actions + " ";
      tempMessage += 'Action Items';
      messageArray.push(tempMessage);
    }
    if( this.elementsDeletedCounter.siStandards !== 0 ) {
      tempMessage = this.elementsDeletedCounter.siStandards + " ";
      tempMessage += 'Standards';
      messageArray.push(tempMessage);
    }
    if( this.elementsDeletedCounter.siGeneralEducationElements !== 0 ) {
      tempMessage = this.elementsDeletedCounter.siGeneralEducationElements + " ";
      tempMessage += 'General Education Elements';
      messageArray.push(tempMessage);
    }
    if( this.elementsDeletedCounter.siInstitutionalPriorities !== 0 ) {
      tempMessage = this.elementsDeletedCounter.siInstitutionalPriorities + " ";
      tempMessage += 'Institutional Priorities';
      messageArray.push(tempMessage);
    }
    if( this.elementsDeletedCounter.siStrategicInitiatives !== 0 ) {
      tempMessage = this.elementsDeletedCounter.siStrategicInitiatives + " ";
      tempMessage += 'Strategic Initiatives';
      messageArray.push(tempMessage);
    }
    return messageArray.join(', ');
  };

  initializeDeleteCounters() {
    this.elementsDeletedCounter.goals = 0;
    this.elementsDeletedCounter.measures = 0;
    this.elementsDeletedCounter.outcomes = 0;
    this.elementsDeletedCounter.targets = 0;
    this.elementsDeletedCounter.actionPlans = 0;
    this.elementsDeletedCounter.actions = 0;
    this.elementsDeletedCounter.siStandards = 0;
    this.elementsDeletedCounter.siGeneralEducationElements = 0;
    this.elementsDeletedCounter.siInstitutionalPriorities = 0;
    this.elementsDeletedCounter.siStrategicInitiatives = 0;
    this.elementsDeletedCounter.slots = 0;
  };

  countDeleteElements(element) {
    if( element.goalObjectiveList ) {
      this.elementsDeletedCounter.goals++; //goal
      element.goalObjectiveList.forEach((countObjective) => {
        this.countObjective(countObjective);
      });
    } else if( element.measureList ) {
      this.countObjective(element);
    } else if( element.targetList ) {
      this.countMeasure(element);
    } else if( element.actionItemList ) {
      this.countActionPlan(element);
    }
  };

  countObjective(objective) {
    if(objective && objective.id) {
      this.elementsDeletedCounter.outcomes++; //count this objective
      if( objective.measureList && objective.measureList.length ) {
        objective.measureList.forEach((measure) => {
          this.countMeasure(measure);
        });
      }
      this.countActionPlan(objective.actionPlan);
      this.countItemsToDelete(objective.siStandardsList, "siStandards");
      this.countItemsToDelete(objective.siGeneralEducationElementsList, "siGeneralEducationElements");
      this.countItemsToDelete(objective.siInstitutionalPrioritiesList, "siInstitutionalPriorities");
      this.countItemsToDelete(objective.siStrategicInitiativesList, "siStrategicInitiatives");
      this.countSlotUses(objective);
    }
  };

  getSlots(projectRequestKey:string = ""):Observable<boolean> {
    if( projectRequestKey === "" ) {
      projectRequestKey = this.projectRequestKeySource.getValue();
    }
    let subject = new Subject<boolean>();
    this.slots = [];
    //call the remote to get the slots
    this.apiCallService.getData('/aeAMSgetMapSlots/', {requestKey : projectRequestKey}).subscribe((response) => {
      if( response.success && response.data[ 0 ] ) {
        response.data[0].dataSet.forEach(slot => {
          this.slots.push(slot);
        });
        subject.next(true);
        return subject;
      } else {
        subject.next(false);
        return subject;
      }
    });
    subject.next(false);
    return subject;
  }

  countSlotUses(objective:AssessmentObjective) {
    if(objective && objective.id) {
      if(this.slots.length > 0) {
        this.slots.forEach( (slot) => {
          if( slot.outcomeList.length > 0 ) {
            slot.outcomeList.forEach( slotOutcome => {
              if( slotOutcome.requestKey == objective.requestKey ) {
                this.elementsDeletedCounter.slots++;
              }
            });
          }
        });
      }
    }
  }

  countMeasure(measure) {
    if(measure && typeof(measure) !== "undefined") {
      this.elementsDeletedCounter.measures++; //count this measure
      this.countItemsToDelete(measure.targetList, "targets");
    }
  };

  countActionPlan(actionPlan) {
    if(actionPlan && actionPlan.id) {
      this.elementsDeletedCounter.actionPlans++; //count this actionPlan
      this.countItemsToDelete(actionPlan.actionItemList, "actions");
    }
  };

  countItemsToDelete(list, itemType) {
    if(list && list.length) {
      list.forEach((item) => {
        this.elementsDeletedCounter[itemType]++; //count items
      });
    }
  };

  /* saves */
  saveEffectProjectMission(mission: string, projectRequestKey: string, assessmentMissionVisionObject) {
    assessmentMissionVisionObject.missionDisabled = true;
    mission = this.trimText(mission);
    return this.apiCallService.postData('/remotes/saveEffectProjectMission/', {pcRequestKey: projectRequestKey, mission : mission}).subscribe(
      (response) => {
      assessmentMissionVisionObject.missionDisabled = false;
      if(response && response.success) {
        return true;
      }
      else{
        if(response && !response.success) return response.dataError[0].description;
        else return 'Mission was not saved. Please try again later';
      }
    });
  };

  saveEffectProjectVision(vision:string, projectRequestKey: string, assessmentMissionVisionObject) {
    assessmentMissionVisionObject.visionDisabled = true;
    vision = this.trimText(vision);
    return this.apiCallService.postData('/remotes/saveEffectProjectVision/', {pcRequestKey : projectRequestKey, vision : vision}).subscribe(
      (response) => {
      assessmentMissionVisionObject.visionDisabled = false;
      if(response && response.success) return true;
      else{
        if(response && !response.success) return response.dataError[0].description;
        else return 'Vision was not saved. Please try again later';
      }
    });
  };

  saveEffectProjectTitle(title, projectRequestKey: string, assessmentHeaderObject) {
    if(!title) return 'Please enter project title';
    title = this.trimText(title);
    assessmentHeaderObject.nameDisabled = true;
    return this.apiCallService.postData('/remotes/saveEffectProjectTitle/', {pcRequestKey : projectRequestKey, title : title }).subscribe((response) => {
      assessmentHeaderObject.nameDisabled = false;
      if(response && response.success) return true;
      else{
        if(response && !response.success) return response.dataError[0].description;
        else return 'Project title was not saved. Please try again later';
      }
    });
  };

  saveGoal( goal: AssessmentGoal, maxSortOrder: string, project: AssessmentProject, template: AssessmentTemplate, inputElement:string) {
    let isNew = false;
    if( goal === null ) {
      goal = new AssessmentGoal();
      goal.sortOrder = this.increaseMaxSortOrder( maxSortOrder );
      goal.newAddedID = new Date().getTime();
      isNew = true;
    }
    let postObject = {
      projectRequestKey: goal.requestKey ? String() : project.requestKey,
      data: JSON.stringify( {
        requestKey: goal.requestKey,
        sortOrder: goal.sortOrder,
        goalData: goal.goalData
      } ),
      goalObjectiveList: goal.goalObjectiveList
    };
    switch(inputElement) {
      case "title":
        goal.titleDisabled = true;
        break;
      case "description":
        goal.descriptionDisabled = true;
        break;
      default:
        goal.titleDisabled = true;
        goal.descriptionDisabled = true;
        break;
    }
    let promise = this.apiCallService.postData( '/remotes/saveEffectProjectGoal/', postObject );
    return promise.subscribe(
      ( response ) => {
        if( response && response.success ) {
          let tempRequestKey = goal.requestKey;
          goal.requestKey = response.data[ 0 ].dataSet.requestKey;
          goal.id = response.data[ 0 ].dataSet.id;
          setTimeout(() =>{
            goal.newAddedID = 0;
          }, 10000);

          if( isNew ) {
            if( !project.goalList ) {
              project.goalList = [ goal ];
            } else {
              project.goalList.push( goal );
            }
            this.projectSource.next(project);
            this.scrollToElem( goal );
          }
          switch ( inputElement ) {
            case "title":
              goal.titleDisabled = false;
              break;
            case "description":
              goal.descriptionDisabled = false;
              break;
            default:
              goal.titleDisabled = false;
              goal.descriptionDisabled = false;
              break;
          }
          this.goalSource.next(goal);
          return 1;
        } else {
          if( postObject.projectRequestKey ) {
            /*this.project.goalList = this.project.goalList.filter(function (g) {
              return g.requestKey !== goal.requestKey;
            }); //todo */
          }
          let message = "There was a problem with saving the " + template[ 'goalCaption' ] ? template[ 'goalCaption' ] : 'Goal';
          if( isNew ) {
            message = "There was a problem with creating a new " + template[ 'goalCaption' ] ? template[ 'goalCaption' ] : 'Goal';
          }
          this.errorMessage.showErrorModal( message, response.dataError );
          switch ( inputElement ) {
            case "title":
              goal.titleDisabled = false;
              break;
            case "description":
              goal.descriptionDisabled = false;
              break;
            default:
              goal.titleDisabled = false;
              goal.descriptionDisabled = false;
              break;
          }
          return false;
        }
      }
    );
  };

  saveObjective( objective: AssessmentObjective, parent, maxSortOrder, template: AssessmentTemplate, inputElement:string, projectRequestKey:string = "" ):Observable<boolean> {
    let subject = new Subject<boolean>();
    let isNew = false;
    if( objective === null || objective.requestKey === null) {
      objective = new AssessmentObjective();
      objective.objectiveData.outcomeType = "objective";
      objective.newAddedID = new Date().getTime();
      isNew = true;
    }
    let parentRequestKey = "";
    if( parent != null) {
      parentRequestKey = parent.requestKey;
    }
    let postObject = {
      goalRequestKey: objective.requestKey ? String() : parentRequestKey,
      projectRequestKey: projectRequestKey,
      data: JSON.stringify( {
        requestKey: objective.requestKey,
        sortOrder: objective.sortOrder,
        objectiveData: objective.objectiveData
      } )
    };
    switch(inputElement) {
      case "title":
        objective.titleDisabled = true;
        break;
      case "description":
        objective.descriptionDisabled = true;
        break;
      default:
        objective.titleDisabled = true;
        objective.descriptionDisabled = true;
        break;
    }
    let promise = this.apiCallService.postData( '/remotes/saveEffectProjectObjective/', postObject );
    promise.subscribe(
      ( response ) => {
        if( response && response.success ) {
          //let tempRequestKey = objective.requestKey;
          objective.requestKey = response.data[ 0 ].dataSet.requestKey;
          objective.id = response.data[ 0 ].dataSet.id;
          setTimeout(() =>{
            objective.newAddedID = 0;
          }, 10000);
          //bodyExpanded[ objective.requestKey ] = bodyExpanded[ tempRequestKey ]; //Keep the state expanded or not expanded
          if( isNew ) {
            if( parent!== null) {
              parent.goalObjectiveList.push( objective );
              this.scrollToElem( objective );
              //scroll to element
            } else {
              this.scrollToElem( objective );
              //todo figure out what to do in assessment view
            }
          } else {
            if(parent !== null ) {
              parent.goalObjectiveList.forEach( ( parentObjective, index ) => {
                if( parentObjective.requestKey === objective.requestKey ) {
                  parent.goalObjectiveList[ index ].objectiveData.title = objective.objectiveData.title;
                }
              });
            }
          }
          switch ( inputElement ) {
            case "title":
              objective.titleDisabled = false;
              break;
            case "description":
              objective.descriptionDisabled = false;
              break;
            default:
              objective.titleDisabled = false;
              objective.descriptionDisabled = false;
              break;
          }
          subject.next(true);
          return subject.asObservable();
        } else {
          if( postObject.goalRequestKey ) {
            parent.goalObjectiveList = parent.goalObjectiveList.filter( function( o ) {
              return o.requestKey !== objective.requestKey;
            } );
          }
          let objectiveCaption = "Outcome";
          if( template !== null ) {
            objectiveCaption = template[ 'objectiveCaption' ];
          }
          this.errorMessage.showErrorModal( "There was a problem with saving the " + objectiveCaption ? objectiveCaption : 'Outcome', response.dataError );
          switch ( inputElement ) {
            case "title":
              objective.titleDisabled = false;
              break;
            case "description":
              objective.descriptionDisabled = false;
              break;
            default:
              objective.titleDisabled = false;
              objective.descriptionDisabled = false;
              break;
          }
          subject.next(false);
          return subject.asObservable();
        }
      } );
    subject.next(false);
    return subject.asObservable();
  };


  saveMeasure(measure: AssessmentMeasure, parent, template: AssessmentTemplate, maxSortOrder:string, inputElement:string) {
    let isNew = false;
    if(measure === null) {
      parent.maxSortOrder = parent.hasOwnProperty('maxSortOrder') ? parent.maxSortOrder : parseInt(maxSortOrder) - 1;
      measure = new AssessmentMeasure();
      measure.sortOrder = this.increaseMaxSortOrder(parent.maxSortOrder);
      measure.newAddedID = new Date().getTime();
      measure.id = 0;
      isNew = true;
    }
    let postObject = {
      objectiveRequestKey : measure.requestKey ? String() : parent.requestKey,
      data : JSON.stringify({
        requestKey : measure.requestKey,
        sortOrder : measure.sortOrder,
        sourceOfEvidence : measure.sourceOfEvidence,
        measureData : measure.measureData
      })
    };
    switch(inputElement) {
      case "title":
        measure.titleDisabled = true;
        break;
      case "description":
        measure.descriptionDisabled = true;
        break;
      case "methodology":
        measure.methodologyDisabled = true;
        break;
      default:
        measure.titleDisabled = true;
        measure.descriptionDisabled = true;
        break;
    }
    measure.fieldsDisabled = true;
    let promise = this.apiCallService.postData('/remotes/saveEffectProjectMeasure/', postObject);
    return promise.subscribe(
      ( response ) => {
      measure.fieldsDisabled = false;
      if(response && response.success){
        let tempRequestKey = measure.requestKey;
        measure.requestKey = response.data[0].dataSet.requestKey;
        measure.id = response.data[0].dataSet.id;
        setTimeout(() =>{
          measure.newAddedID = 0;
        }, 10000);
        //bodyExpanded[measure.requestKey] = bodyExpanded[tempRequestKey]; //Keep the state expanded or not expanded
        if(isNew) {
          if (!parent.measureList) {
            parent.measureList = [measure];
          } else {
            parent.measureList.push( measure );
            this.scrollToElem( measure );
          }
        }

        switch ( inputElement ) {
          case "title":
            measure.titleDisabled = false;
            break;
          case "description":
            measure.descriptionDisabled = false;
            break;
          case "methodology":
            measure.methodologyDisabled = false;
            break;
          default:
            measure.titleDisabled = false;
            measure.descriptionDisabled = false;
            break;
        }
        return 1;
      }
      else{
        if(postObject.objectiveRequestKey){
          parent.measureList = parent.measureList.filter(function (o) {
            return o.requestKey !== measure.requestKey;
          });
        }
        this.errorMessage.showErrorModal("There was a problem with saving the " + template['measureCaption'] ? template['measureCaption'] : 'Measure', response.dataError);
        switch ( inputElement ) {
          case "title":
            measure.titleDisabled = false;
            break;
          case "description":
            measure.descriptionDisabled = false;
            break;
          case "methodology":
            measure.methodologyDisabled = false;
            break;
          default:
            measure.titleDisabled = false;
            measure.descriptionDisabled = false;
            break;
        }
        return false;
      }
    });
  };

  saveTarget(target: AssessmentTarget, parent, template: AssessmentTemplate, maxSortOrder:string) {
    let isNew = false;
    if(target === null) {
      parent.maxSortOrder = parent.hasOwnProperty('maxSortOrder') ? parent.maxSortOrder : parseInt(maxSortOrder) - 1;
      target = new AssessmentTarget();
      isNew = true;
    }
    let postObject = {
      measureRequestKey : target.requestKey ? String() : parent.requestKey,
      targetData : JSON.stringify({
        targetData : target.targetData,
        targetType: String(),
        improvementsAchieved : target.improvementsAchieved,
        achievementStatus : target.achievementStatus,
        sortOrder : target.sortOrder,
        requestKey: target.requestKey,
        name: target.name
      })
    };
    target.fieldsDisabled = true;
    let promise = this.apiCallService.postData('/remotes/saveEffectProjectTarget/', postObject);
    return promise.subscribe(
      (response) => {
      target.fieldsDisabled = false;
      if(response && response.success){
        let tempRequestKey = target.requestKey;
        target.requestKey = response.data[0].dataSet.requestKey;
        target.improvementsAchievedType = this.getElemObjectByRequestkey(this.improvementsAchievedListUntouched, target.improvementsAchieved).type.requestKey;
        //bodyExpanded[target.requestKey] = bodyExpanded[tempRequestKey]; //Keep the state expanded or not expanded
        if(isNew) {
          if (!parent.targetList) {
            parent.targetList = [target];
          } else {
            parent.targetList.push( target );
            this.scrollToElem( target );
          }
        }
        return true;
      } else{
        if(postObject.measureRequestKey){
          parent.targetList =  parent.targetList.filter(function (t) {
            return t.requestKey !== target.requestKey;
          });
        }
        this.errorMessage.showErrorModal("There was a problem with saving the " + template['qualitativeTargetCaption'] ? template['qualitativeTargetCaption'] : 'Target', response.dataError);
        return false;
      }
    });
  };

  saveActionPlan(actionPlan: AssessmentActionPlan , parent: AssessmentObjective, template) {
    let isNew = false;
    if( actionPlan === null ) {
      isNew = true;
      actionPlan = new AssessmentActionPlan();
    }
    let postObject = {
      objectiveRequestKey : parent.requestKey,
      pcRequestKey : actionPlan.requestKey,
      description : actionPlan.description,
      dueDate : actionPlan.dueDate.toString(),
      improvementPlanStatusID : actionPlan.improvementPlanStatusID,
      budgetAllocationAmount : actionPlan.budgetAllocationAmount,
      budgetAllocationDescription : actionPlan.budgetAllocationDescription
    };
    actionPlan.fieldsDisabled = false;
    let promise = this.apiCallService.postData('/remotes/saveEffectProjectActionPlan/', postObject);
    return promise.subscribe((response) => {
      actionPlan.fieldsDisabled = false;
      if(response && response.success){
        actionPlan.requestKey = response.data[0].dataSet.requestKey;
        //let tempRequestKey = actionPlan.requestKey;
        //this.bodyExpanded[actionPlan.requestKey] = this.bodyExpanded[tempRequestKey]; //Keep the state expanded or not expanded
        if( isNew ) {
          //this.bodyExpanded[actionPlan.requestKey] = true;
          actionPlan.id = response.data[0].dataSet.requestKey;
          parent.actionPlan = actionPlan;
          this.scrollToElem( actionPlan );
        }
        return 1;
      } else {
        if(postObject.objectiveRequestKey){
          parent.actionPlan =  new AssessmentActionPlan(); //todo ??
        }
        this.errorMessage.showErrorModal("There was a problem with saving the " + template['actionPlanCaption'] ? template['actionPlanCaption'] : 'Action Plan', response.dataError);
        return false;
      }
    });
  };

  saveActionPlanItem(actionPlanItem: AssessmentActionPlanItem, parent: AssessmentActionPlan) {
    let isNew  = false;
    if( actionPlanItem === null ) {
      actionPlanItem = new AssessmentActionPlanItem();
      actionPlanItem.itemNumber = parent.actionItemList && Array.isArray(parent.actionItemList) ? parent.actionItemList.length : 0;
      isNew = true;
    }
    let postObject = {
      actionPlanRequestKey : actionPlanItem.requestKey ? String() : parent.requestKey,
      description : actionPlanItem.description,
      dueDate : actionPlanItem.dueDate ? new Date(Date.parse(actionPlanItem.dueDate)).toDateString() : String(),
      improvementPlanStatusID : isNew? "": actionPlanItem.improvementPlanStatusID,
      itemNumber: actionPlanItem.itemNumber,
      pcRequestKey : isNew? "" : actionPlanItem.requestKey
    };
    actionPlanItem.fieldsDisabled = true;
    let promise = this.apiCallService.postData('/remotes/saveEffectProjectActionPlanItem/', postObject);
    return promise.subscribe((response) => {
      actionPlanItem.fieldsDisabled = false;
      if(response && response.success) {
        actionPlanItem.requestKey = response.data[0].dataSet.requestKey;
        //this.bodyExpanded[actionPlanItem.requestKey] = this.bodyExpanded[tempRequestKey]; //Keep the state expanded or not expanded
        if(isNew) {
          actionPlanItem.dateAdded = new Date().toISOString();
          //this.bodyExpanded[actionPlanItem.requestKey] = true;
          if(!parent.actionItemList) {
            parent.actionItemList = [actionPlanItem];
          } else {
            parent.actionItemList.push( actionPlanItem );
          }
        }
        return 1;
      } else {
        if(postObject.actionPlanRequestKey){
          parent.actionItemList =  parent.actionItemList.filter((a) => {
            return a.requestKey !== actionPlanItem.requestKey;
          });
        }
        this.errorMessage.showErrorModal("There was a problem with saving the action item", response.dataError);
        return false;
      }
    });
  };

  /*
  Function saveOutlineProject: will save the assessment object, then return to normal view. Will copy the changes to the backup project also
  */
  saveOutlineProject(project): Observable<RemoteResponse>{
      let subject = new Subject<RemoteResponse>();
      this.apiCallService.postJsonData("/aeAMSsaveAssessmentProject/", {projectData : this.minifyAssessmentProjectObject(project)}).subscribe((response) => {
        if (response.success) {
          //Do some staff
          this.projectSource.next(project)
        }
        else{
          this.errorMessage.showErrorModal("There was a problem with saving the project order", response.dataError);
        }
        subject.next(response);
      });
      return subject.asObservable();
  };



  /*
  Function minifyAssessmentProjectObject: will convert the assessment project to what we just need to post to the server
   */
  minifyAssessmentProjectObject(project: AssessmentProject){
    var minifiedObject = {
      id: project.id,
      requestKey: project.requestKey,
      name: project.name,
      projectStatus: project.projectStatus.id,
      goalList: []
    };
    project.goalList.forEach((goal: AssessmentGoal) => {
      let minifiedGoal = {
        id: goal.id,
        requestKey: goal.requestKey,
        name: goal.goalData.title,
        goalData: goal.goalData,
        goalObjectiveList: []
      }
      goal.goalObjectiveList.forEach((outcome: AssessmentObjective) => {
        let minifiedOutcome = {
          id: outcome.id,
          requestKey: outcome.requestKey,
          referenceILOGuid: outcome.referenceILOGuid,
          name: outcome.name,
          objectiveData: outcome.objectiveData,
          measureList: []
        };
        outcome.measureList.forEach((measure: AssessmentMeasure) => {
          let minifiedMesure = {
            id: measure.id,
            requestKey: measure.requestKey,
            name: measure.name,
            measureData: measure.measureData,
            referenceILOMeasureGuid: measure.referenceILOMeasureGuid
          };
          minifiedOutcome.measureList.push(minifiedMesure);
        });
        minifiedGoal.goalObjectiveList.push(minifiedOutcome);
      });
      minifiedObject.goalList.push(minifiedGoal);
    });
    return minifiedObject;
  };




  getElemObjectByRequestkey = function (list, requestKey) {
    var i = list.filter(function (i) {
      return i.requestKey === requestKey;
    });
    if(Array.isArray(i) && i.length) return i[0];
    return {
      type: {
        requestKey: ""
      }
    };
  };


  updateAchievementStatuses( list ) {
    let newList = this.emptyOption.concat(list);
    newList.forEach(listItem => {
      listItem.class = this.statusColors[ listItem.requestKey ];
    });
    this.setAchievementStatusesList(newList);
  }

  updateSourcesOfEvidence( list ) {
    list.forEach((item)=>{
      if(item.type === null) {
        item.type = {name:""};
      }
    });
    list = this.sortList(list);
    list = this.emptyOption.concat(list);
    this.setSourcesOfEvidence(list);
  }

  sortList(list) {
    list.sort(function (a, b) {
      if(a.type.requestKey === b.type.requestKey){
        if(a.description < b.description) return -1;
        if(a.description > b.description) return 1;
      }
      else{
        if(a.type.name < b.type.name) return -1;
        if(a.type.name > b.type.name) return 1;
      }
      return 0;
    });
    return list;
  }

  updateImprovementsAchievedList( list ) {
    //sort the list
    this.improvementsAchievedListUntouched = list;
    list = this.sortList(list);
    list = this.emptyImprovementList.concat(list);

    //group the list
    let types = [];
    list.forEach((listItem) => {
      if( !this.typeExists( types, listItem.type.requestKey ) ) {
        types.push(listItem.type);
      }
    });
    list.forEach((listItem) => {
      types.forEach((groupItem) => {
        if(groupItem.requestKey === listItem.type.requestKey) {
          if(Array.isArray(groupItem.items)) {
            groupItem.items.push( listItem );
          } else {
            groupItem.items= [listItem];
          }
        }
      });
    });

    this.setImprovementsAchievedList(types);
  }

  typeExists(types, requestKey) {
    let found = false;
    types.forEach((item)=>{
      if(item.requestKey == requestKey) {
        found = true;
        return true;
      }
    });
    return found;
  }


  convertProject(project: any) {
    let newProject: AssessmentProject = null;
    if(project !== null ) {
      newProject = new AssessmentProject(
        project.id,
        project.requestKey,
        project.resourceUrl,
        project.active,
        project.ILOCount,
        project.planTypeId,
        project.name,
        project.authors,
        project.projectStatus.id,
        "", //todo
        "",
        "",
        "",
        project.projectStatus.name,
        project.projectStatus.requestKey,
        project.projectStatus.caption,
        project.projectStatus.editable,
        project.projectStatus.active,
        project.reportingPeriod.deletable,
        project.reportingPeriod.editable,
        project.reportingPeriod.description,
        project.reportingPeriod.type,
        project.reportingPeriod.requestKey,
        project.reportingPeriod.id,
        project.reportingPeriod.name,
        project.reportingPeriod.active,
        project.projectData.mission,
        project.projectData.vision,
        [], //goalList
        [], //outcomesList
        [], //measuresList
        project.sourcesOfEvidence, //todo also class
        project.sourceID,
        project.ownerName,
        project.ownerRequestKey,
        project.authorList,
        project.canRemoveTeamMember,
        project.editLockMember,
        project.editLocked,
        project.hasAccess,
        project.isOwner,
        project.isReviewer,
        project.isAuthor,
        project.organization.id,
        project.organization.requestKey,
        project.organization.name,
        project.projectTemplateID,
        project.projectTemplate, //todo also class
        project.projectAttachmentList, //todo also class
        project.relatedProjectList,
        project.siStandardsList, //todo also class
        project.siGeneralEducationElementsList, //todo also class
        project.siInstitutionalPrioritiesList, //todo also class
        project.siStrategicInitiativesList, //todo also class
        project.projectHtml,
        project.calculatedResourceURL,
        project.sortOrder,
        project.nameDisabled,
      );

      //goals
      project.goalList.forEach( goal => {
        let newGoal = new AssessmentGoal(
          goal.id,
          goal.goalData.title,
          goal.goalData.description,
          goal.requestKey,
          goal.sortOrder,
          goal.newAddedID,
          [] //objectives
        );
        //objectives
        goal.goalObjectiveList.forEach( objective => {
          //action plan
          let newActionPlan = new AssessmentActionPlan(
            newGoal.htmlId,
            objective.actionPlan.id,
            objective.actionPlan.requestKey,
            objective.actionPlan.improvementPlanStatusID,
            objective.actionPlan.name,
            objective.actionPlan.description,
            objective.actionPlan.dueDate,
            objective.actionPlan.budgetAllocationDescription,
            objective.actionPlan.budgetAllocationAmount,
            objective.actionPlan.actionItemList
          );

          let newObjective = new AssessmentObjective(
            newGoal.htmlId,
            objective.id,
            objective.requestKey,
            objective.name,
            objective.active,
            objective.sortOrder,
            objective.objectiveData.title,
            objective.objectiveData.description,
            objective.objectiveData.outcomeType,
            objective.objectiveData.budgetAllocationDescription,
            objective.objectiveData.budgetAllocationAmount,
            [], //measureList
            objective.siStandardsList, //as is for now
            objective.siGeneralEducationElementsList, //as is for now
            objective.siInstitutionalPrioritiesList, //as is for now
            objective.siStrategicInitiativesList, //as is for now
            objective.referenceILOGuid,
            0,
            objective.creationDate,
            newActionPlan
          );
          //measures
          objective.measureList.forEach( measure => {
            let newMeasure:AssessmentMeasure = new AssessmentMeasure(
              objective.htmlId,
              measure.id,
              measure.requestKey,
              true,
              measure.active,
              measure.sortOrder,
              measure.measureData.measure,
              measure.measureData.description,
              measure.measureData.methodology,
              measure.measureData.analysisOfFinding,
              measure.measureSourceOfEvidence,
              measure.sourceOfEvidence,
              [],//targetList
              measure.referenceILOMeasureGuid
            );

            //targets
            if(measure.targetList !== null ) {
              measure.targetList.forEach( target => {
                let newTarget: AssessmentTarget = new AssessmentTarget(
                  newMeasure.htmlId,
                  target.id,
                  target.requestKey,
                  true,
                  target.pcProjectObjectiveMeasureID,
                  target.targetType,
                  target.targetData.description,
                  target.targetData.finding,
                  target.targetData.analysisOfFinding,
                  target.targetData.improvementsAchieved,
                  target.targetData.achievementsAchieved,
                  target.improvementsAchieved,
                  target.achievementStatus,
                  target.referenceILOTargetGuid,
                  target.healthy,
                  target.healthyFinding,
                  target.sortOrder,
                  target.name,
                  "" //todo get status class
                );
                newMeasure.targetList.push( newTarget );
              });
            }
            newObjective.measureList.push( newMeasure );
          } );
          newGoal.goalObjectiveList.push( newObjective );
        } );
        newProject.goalList.push( newGoal );
      } );
      //objectives without goal
      project.objectiveList.forEach( outcome => {
        let newOutcome = new AssessmentObjective(
          "",
          outcome.id,
          outcome.requestKey,
          outcome.name,
          true,
          "",
          outcome.objectiveData.title,
          outcome.objectiveData.description,
          outcome.objectiveType,
          outcome.objectiveData.budgetAllocationDescription,
          outcome.objectiveData.budgetAllocationAmount,
          [], //measureList probably always empty?
          outcome.siStandardsList, //as is for now
          outcome.siGeneralEducationElementsList, //as is for now
          outcome.siInstitutionalPrioritiesList, //as is for now
          outcome.siStrategicInitiativesList, //as is for now
          outcome.referenceILOGuid,
          0,
          outcome.creationDate
        )
        newProject.outcomesList.push( newOutcome );
      } );

      if(project.measureList !== undefined ) {
        project.measureList.forEach( measure => {
          let newUnassignedMeasure = new AssessmentMeasure(
            "",
            measure.id,
            measure.requestKey,
            true,
            true,
            "",
            measure.name
          );
          newProject.measuresList.push( newUnassignedMeasure );
        } );
      }
    }
    return newProject;
  }

  getAvailableReportingPeriods(requestKey:string) {
    this.apiCallService.getData('/remotes/getEffectProjectsByReportingPeriod/', {pcRequestKey : requestKey}).subscribe(
      (response) => {
        if (response.success && response.data[0]) {
          this.reportingPeriodsSource.next(response.data[0].dataSet);
        }
        else{
          this.errorMessage.showErrorModal("There was a problem with getting the available reporting periods for this project", response.dataError);
        }
      });
  };

  trimText(text:string) {
    if(text === " ") { //remove emptiness
      text = "";
    }
    if(text === "<br>") { //remove emptiness
      text = "";
    }
    return text.trim();
  }

  getViewStates(projectRequestKey:string) {
    if(projectRequestKey) {
      let projectViewStates = new ProjectViewStates();
      let viewStatesFromStorage = this.getFromLocalStorage( projectRequestKey, "weaveViewStates" );
      if( viewStatesFromStorage !== undefined ) {
        let parsedStates = JSON.parse( viewStatesFromStorage );
        projectViewStates.leftClosed = parsedStates.leftClosed;
        projectViewStates.rightClosed = parsedStates.rightClosed;
        projectViewStates.rightMax = parsedStates.rightMax;
        this.updateLeftClosed( projectViewStates.leftClosed, projectRequestKey );
        this.updateRightClosed( projectViewStates.rightClosed, projectRequestKey );
        this.updateRightMax( projectViewStates.rightMax, projectRequestKey );
      }
    }
  }

  setViewStates(projectRequestKey: string) {
    let projectViewStates = new ProjectViewStates();
    projectViewStates.leftClosed = this.leftClosedSource.getValue();
    projectViewStates.rightClosed = this.rightClosedSource.getValue();
    projectViewStates.rightMax = this.maxRightSource.getValue();
    this.saveToLocalStorage(projectRequestKey, "weaveViewStates", JSON.stringify(projectViewStates));
  }

  //local storage related functions, todo probably should be moved elsewhere
  saveToLocalStorage(projectRequestKey:string, key:string, data) {
    let record: Record<string, string> = this.getAllLocalStorage(key);
    record[projectRequestKey] = data;
    localStorage.setItem(key, JSON.stringify(record));
  }
  getFromLocalStorage(projectRequestKey: string, key: string):string {
    return this.getAllLocalStorage(key)[projectRequestKey];
  }
  private getAllLocalStorage(key:string): Record<string, string>{
    let storageJSONString = localStorage.getItem(key);
    if(!storageJSONString) {
      return {};
    }
    return JSON.parse(storageJSONString);
  }


  getAllExpandStates(): Record<string, projectExpandStates>{
    let expandStatesJSONString = localStorage.getItem("weaveProjectsExpandStates");
    if(!expandStatesJSONString){
      return {};
    }
    return JSON.parse(expandStatesJSONString);
  }

  getExpandStates(projectRequestKey): projectExpandStates{
    return this.getAllExpandStates()[projectRequestKey];
  }

  saveExpandStates(projectRequestKey, projectExpandedStates: projectExpandStates){
    let expandStates: Record<string, projectExpandStates> = this.getAllExpandStates();
    expandStates[projectRequestKey] = projectExpandedStates;
    localStorage.setItem("weaveProjectsExpandStates", JSON.stringify(expandStates));
  }

  projectLoadExpandedStates(project: AssessmentProject, states: projectExpandStates){
    project.projectData.missionExpanded = states.missionExpanded;
    project.projectData.visionExpanded = states.visionExpanded;
    project.projectData.expandedAll = states.expandedAll;
    project.projectData.attachmentsExpanded = states.attachmentsExpanded;
    project.goalList.forEach((goal) => {
      let goalExpandedStates = states.goals[goal.requestKey];
      goal.expanded = goalExpandedStates ? goalExpandedStates.expanded : true;
      if(goalExpandedStates && goalExpandedStates.outcomes){
        goal.goalObjectiveList.forEach((objective) => {
          let objectiveExpandedStates = goalExpandedStates.outcomes[objective.requestKey];
          objective.expanded = objectiveExpandedStates ? objectiveExpandedStates.expanded : true;
          objective.supportedInitiativesExpanded = objectiveExpandedStates ? objectiveExpandedStates.supportedInitiativeExpanded : true;
          objective.actionPlan.expanded = objectiveExpandedStates ? objectiveExpandedStates.actionPlanExpanded : true;
          if(objectiveExpandedStates && objectiveExpandedStates.measures){
            objective.measureList.forEach((measure) => {
              let measureExpandedStates = objectiveExpandedStates.measures[measure.requestKey];
              measure.expanded = measureExpandedStates ? measureExpandedStates.expanded : true;
              if(measureExpandedStates && measureExpandedStates.targets){
                measure.targetList.forEach((target) => {
                  let targetExpandedStates = measureExpandedStates.targets[target.requestKey];
                  target.expanded = targetExpandedStates ? targetExpandedStates.expanded : true;
                })
              }
            })
          }
        })
      }
    })
  }

  updateExpandedStatesFromProject(states: projectExpandStates, project: AssessmentProject, defaultState: boolean){
    states.projectRequestKey = project ? project.requestKey : String();
    states.missionExpanded = defaultState ? true : project.projectData.missionExpanded;
    states.visionExpanded = defaultState ? true : project.projectData.visionExpanded;
    states.expandedAll = defaultState ? true : project.projectData.expandedAll;
    states.attachmentsExpanded =  defaultState ? true : project.projectData.attachmentsExpanded;
    states.goals = {};
    if(project) {
      project.goalList.forEach((goal) => {
        states.goals[goal.requestKey] = {
          expanded: defaultState ? true : goal.expanded,
          outcomes: {}
        }

        let currentStateGoal = states.goals[goal.requestKey];
        goal.goalObjectiveList.forEach((objective) => {
          currentStateGoal.outcomes[objective.requestKey] = {
            expanded: defaultState ? true : objective.expanded,
            supportedInitiativeExpanded: defaultState ? true : objective.supportedInitiativesExpanded,
            actionPlanExpanded: defaultState ? true : objective.actionPlan.expanded,
            measures: {}
          }

          let currentStateOutcome = currentStateGoal.outcomes[objective.requestKey];
          objective.measureList.forEach((measure) => {
            currentStateOutcome.measures[measure.requestKey] = {
              expanded: defaultState ? true : measure.expanded,
              targets: {}
            }

            let currentStateMeasure = currentStateOutcome.measures[measure.requestKey];
            measure.targetList.forEach((target) => {
              currentStateMeasure.targets[target.requestKey] = {
                expanded: defaultState ? true : target.expanded
              };
            })
          });
        })
      })
    }
  }

  saveUnassignedMeasure(projectRequestKey: string, measure: AssessmentMeasure = new AssessmentMeasure()) {
    let remote = "/aeAMSsaveUnassignedMeasure/";
    let isNew = false;
    if(measure.requestKey === "") { //new measure
      measure.sortOrder = "";
      measure.newAddedID = new Date().getTime();
      measure.id = 0;
      remote = "/aeAMScreateUnassignedMeasure/";
      isNew = true;
    }
    let postObject = {
      requestKey: measure.requestKey,
      objectiveGuid: "",
      pcProjectObjectiveId: 0,
      projectRequestKey : projectRequestKey,
      sourcesOfEvidenceGuid: "",
      PCSourcesOfEvidenceID: 0,
      referenceMeasureGuid: "",
      referenceMeasureID: 0,
      measureData: measure.measureData,
      outlinePrefix: "",
      healthy: true
    };
    let promise = this.apiCallService.postJsonData(remote, postObject);
    return promise.subscribe(
      ( response ) => {
        measure.fieldsDisabled = false;
        if(response && response.success){
          if(isNew) {
            measure.requestKey = response.data[ 0 ].dataSet.requestkey;
            let tempProject = this.projectSource.getValue();
            tempProject.measuresList.push( measure );
            this.projectSource.next( tempProject );
          }
          return true;
        } else {
          if(isNew) {
            this.errorMessage.showErrorModal( "There was a problem creating the new " + this.template[ 'measureCaption' ] ? this.template[ 'measureCaption' ] : 'Measure', response.dataError );
          } else {
            this.errorMessage.showErrorModal( "There was a problem saving the " + this.template[ 'measureCaption' ] ? this.template[ 'measureCaption' ] : 'Measure', response.dataError );
          }
          return false;
        }
      });
  }

  findElementByRequestKey(requestKey: string) {
    let currentProject = this.projectSource.getValue();
    let foundElement = null;
    //check goals
    let results = currentProject.goalList.filter(item => {
      return item.requestKey == requestKey;
    });
    if(results.length > 0) {
      foundElement = results[0];
    }

    if(foundElement == null) {
      currentProject.goalList.forEach( goal => {
        //check objectives
        let results = goal.goalObjectiveList.filter( item => {
          return item.requestKey == requestKey;
        });
        if( results.length > 0 ) {
          foundElement = results[ 0 ];
        }

        if(foundElement == null) {
          goal.goalObjectiveList.forEach( objective => {
            //check measures
            let results = objective.measureList.filter( measure => {
              return measure.requestKey == requestKey;
            } )
            if( results.length > 0 ) {
              foundElement = results[ 0 ];
            }

            if(foundElement == null) {
              objective.measureList.forEach( measure => {
                //check targets
                let results = measure.targetList.filter( target => {
                  return target.requestKey == requestKey;
                } )
                if( results.length > 0 ) {
                  foundElement = results[ 0 ];
                }
              });
              //check action plans
              if(foundElement == null) {
                if(objective.actionPlan.requestKey == requestKey) {
                  foundElement = objective.actionPlan;
                }
              }
            }
          });
        }
      });
    }
    return foundElement;
  }

  reset(projectRequestKey) {
    this.projectDestroyTimeout();
    this.apiCallService.getData( '/remotes/closeProjectEdit/', { pcRequestKey: projectRequestKey } ).pipe(first()).subscribe((response)=>{});
  }

}

export function getAssessmentProject(AssessmentService: AssessmentService) {
  return AssessmentService.project;
}
export function getAssessmentProjectRequestKey(AssessmentService: AssessmentService) {
  return AssessmentService.projectRequestKey;
}
export function getService(AssessmentService: AssessmentService) {
  return AssessmentService.service;
}
